import { sizes as BUTTON_SIZES } from '../components/primitives/button-constants';
import '@mui/material/Button';
import '@mui/material/IconButton';
import '@mui/material/Stack';
import {
  Theme,
  createTheme,
  Shadows,
  SimplePaletteColorOptions,
  PaletteColorOptions,
} from '@mui/material/styles';
import type { ResponsiveStyleValue } from '@mui/system';
import { BRAND, CHROME, NEUTRAL, SIZE, STATUS } from '../../design-system/tokens/tokens.generated';
import { FieldHelpText } from '../components/inputs/field/field-helper-text-caption';
import { SELECTION_ROW_DEFAULTS, type SelectionRowVars } from './selection-row-vars';
import type {} from '@mui/x-date-pickers/themeAugmentation';

const defaultTheme = createTheme();

/**
 * Field colours that must show on an unfocused input. MUI's `color` prop only tints the focus
 * ring, so a field reporting its own state — a warning on the value, or a check that passed — is
 * invisible until someone clicks into it. Both states are driven from one list so the outline, the
 * label and the help text cannot end up supporting different sets.
 */
const FIELD_STATE_COLOURS = ['warning', 'success'] as const;

// Buttons drawn as bare text keep that look when disabled, instead of the outlined root disabled style.
const BORDERLESS_DISABLED = { border: 'none', backgroundColor: 'transparent' };

/** MUI's default root font-size. The one place this file converts a rem token to a number. */
const CSS_ROOT_PX = 16;
const remToPx = (value: string): number =>
  value.endsWith('rem') ? parseFloat(value) * CSS_ROOT_PX : parseFloat(value);

interface StaticVars {
  borderRadiusPx: string;
  headerHeight: number;
  mobileFooterHeight: string;
  flexCenter: {
    display: string;
    alignItems: string;
    justifyContent: string;
  };
  selectionRow: SelectionRowVars;
  /**
   * The colour a field's validation message paints in. A brand knob rather than a fixed
   * `error.main` because the contrast depends on the canvas the brand chose: 12px/400 `#D32F2F`
   * measures 4.98:1 on paper and 4.85:1 on the `#FCFCFC` canvas, but only **4.37:1** on Trackd's
   * `#F0F0F2` — under WCAG AA's 4.5:1 for normal text, on the one app whose fields sit directly on
   * the page background. Trackd opts into the `error.dark` step (8.93:1); every other brand keeps
   * `error.main` and is unchanged.
   */
  fieldErrorText: string;
  fieldMargin: string;
}

declare module '@mui/material/styles' {
  interface Components {
    MuiPickersOutlinedInput?: any;
    MuiPickersTextField?: any;
  }
  interface Theme {
    staticVars: StaticVars;
  }
  interface ThemeOptions {
    staticVars: StaticVars;
  }

  interface SimplePaletteColorOptions {
    accent?: string;
    darkest?: string;
    separator?: string;
  }

  interface PaletteColor {
    accent?: string;
    darkest?: string;
    separator?: string;
  }

  interface Palette {
    neutral: PaletteColor;
  }

  interface PaletteOptions {
    neutral?: PaletteColorOptions;
  }

  interface TypeBackground {
    panel?: string;
  }

  /**
   * A link is body-size text and owes 4.5:1, which `info.main` (the 3:1 UI step) does not have.
   * Required rather than optional on purpose: a link colour that silently resolves `undefined` is
   * exactly how `MuiLink` came to fall back to `info.main` in the first place.
   */
  interface TypeText {
    link: string;
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonPropsColorOverrides {
    'primary-contained': true;
    'primary-outlined': true;
  }
}
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    neutral: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    primary: true;
    secondary: true;
    text: true;
    custom: true;
  }
}

declare module '@mui/material/Stack' {
  interface StackOwnProps {
    /**
     * @deprecated Use `sx={{ gap: ... }}` instead of `spacing`.
     */
    spacing?: ResponsiveStyleValue<number | string> | undefined;
  }
}

export const BorderRadiusPx = '8px';

/**
 * Smallest font-size a text input may use on a touch device, from `size.inputTouchFontMin`
 * (§X: tokens, not a literal). Below 16px iOS force-zooms on focus and does not undo it.
 * Shared by the `MuiInputBase` and MUI X picker overrides below so the two cannot drift.
 */
const NO_FOCUS_ZOOM_FONT_SIZE = SIZE.inputTouchFontMin;

/**
 * The status ramp here is four steps wide and only `main` means what the token of the same name
 * means, so the other three stay as-built rather than being spread from `STATUS`:
 *
 * - `dark` is the **hover fill**, not `status.*.dark`, which is the accessible-text step
 *   (`#5E4610` olive for warning, `#10345E` navy for info). Binding it would repaint every
 *   status hover in four apps a different hue.
 * - `light` is the pale tint fill and `accent` the tint border. They match `status.*.bg` and
 *   `status.*.bright` for success and info and have drifted for warning and error.
 *
 * Reconciling those three is a design decision, not a contrast correction, so it is recorded as
 * an open item in DESIGN-SYSTEM.md §1B instead of guessed at here.
 */
const STATUS_AS_BUILT = {
  warning: { light: '#FEEFE8', dark: '#D9480F', accent: '#FFB57C' },
  info: { light: '#EDF2F7', dark: '#005A9C', accent: '#B6E1FF' },
  success: { light: '#EDF7ED', dark: '#0F6B2A', accent: '#82D57A' },
  error: { light: '#FFEBEB', dark: '#A61C1C', accent: '#FFA0A0' },
} as const;

/** What `MuiLink` painted before `text.link` existed. Typed wide so the knob accepts any colour —
 *  `STATUS` is `as const`, which would otherwise pin the parameter to this one literal. */
const AS_BUILT_LINK: string = STATUS.info.main;

/* Trackd is the canary for the token pipeline (owner decision, 2026-07-28): it is the one brand
 * whose palette is DERIVED from tokens.json rather than hand-copied here. Checkd, Dashd and
 * Manager below keep their literals untouched and are unaffected — Checkd is live.
 *
 * These values are byte-identical to the literals they replace, so Trackd does not change
 * appearance; the point is that the values now have one home. Two notes on the mapping:
 *   - `dark` maps to BRAND.trackd.pressed (#553096), NOT BRAND.trackd.dark (#31077A). The latter
 *     came from the 1:1 Figma extraction and has never been what renders. Which is correct is an
 *     open design question — see theme.trackd.pressed in tokens.json.
 *   - `darkest` has no Figma equivalent; it was recorded into tokens.json as an as-built value.
 */
export const TRACKD_PRIMARY_THEME: SimplePaletteColorOptions = {
  main: BRAND.trackd.main,
  light: BRAND.trackd.light,
  dark: BRAND.trackd.pressed,
  accent: BRAND.trackd.accent,
  darkest: BRAND.trackd.darkest,
  separator: BRAND.trackd.separator,
};

export const CHECKD_PRIMARY_THEME: SimplePaletteColorOptions = {
  main: '#B73193',
  light: '#FAE9F9',
  dark: '#8C0668',
  accent: '#E297CE',
  darkest: '#350327',
  separator: '#E3C8DC',
};

export const DEFAULT_PRIMARY_THEME: SimplePaletteColorOptions = {
  main: '#C42464',
  light: '#FAE9F8',
  dark: '#8C0659',
  contrastText: '#F7EDED',
  accent: '#000',
  darkest: '#1f2024',
};

export const DASHD_PRIMARY_THEME: SimplePaletteColorOptions = {
  main: '#3161D1',
  light: '#DCEBFF',
  dark: '#062C8C',
  accent: '#8AB0F9',
  separator: '#C8D3E3',
};

// Minimal Dashboard inspired theme
export const CustomTheme = ({
  primary = {},
  divider = NEUTRAL.divider,
  // Visual-refresh knobs. Defaults are the ORIGINAL values, so a theme only changes if it
  // opts in. Trackd opts into the design-system refresh; Checkd/Dashd/Manager keep the originals.
  canvas = '#FBFBFB',
  paper = '#FFFFFF',
  inputRadius = BorderRadiusPx,
  fieldMargin = '0.75rem',
  selectionRow = {} as Partial<SelectionRowVars>,
  fieldErrorText = 'error.main',
  /* WCAG 2.2 AA knobs (DESIGN-SYSTEM.md §1B), opt-in for the same reason the refresh knobs above
     are: these three are the only token values whose rendered colour differs from the literal this
     factory has always used, and Checkd is live. Defaults are the ORIGINAL values, so a brand only
     moves if it asks to. Trackd opts in; Checkd, Dashd and Manager resolve byte-identically to what
     they render today — including their own contrast gaps, which are real, measured in §1B, and an
     owner decision to close rather than something to fix in passing. */
  textSecondary = '#6D717F',
  warningMain = '#F26316',
  /* `info.main` is what `MuiLink` painted before `text.link` existed, and the token and the literal
     are the same `#0288D1` — so this default is the as-built link colour, not a new one. */
  linkText = AS_BUILT_LINK,
} = {}) => {
  const { main, light, dark, contrastText, accent, darkest, separator } = {
    ...DEFAULT_PRIMARY_THEME,
    ...primary,
  };

  return createTheme({
    typography: {
      // The name used in the @font-face rule or CDN link
      fontFamily: ['Inter', 'Roboto', '"Helvetica Neue"', 'Arial', 'sans-serif'].join(','),
    },
    // Add custom variables here (add the types above ^)
    staticVars: {
      borderRadiusPx: inputRadius,
      // Derived from tokens, not literals (spec 029 FR-014). Both were hand-maintained copies of a
      // token value, which is the drift the tokens-only rule exists to prevent. `headerHeight` is a
      // number because callers do arithmetic on it, so the rem token is resolved against the MUI
      // default 16px root — NOT the app's root font-size, because that is what the `48` it replaces
      // always meant. `mobileFooterHeight` stays the rem string it was, so each app keeps rendering
      // the height it renders today (56px in Trackd at a 14px root, 64px elsewhere).
      headerHeight: remToPx(CHROME.pageHeader),
      mobileFooterHeight: CHROME.tabBar,
      flexCenter: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
      selectionRow: { ...SELECTION_ROW_DEFAULTS, ...selectionRow },
      fieldErrorText,
      fieldMargin,
    },
    shadows: ['none', '0px 4px 14px 0px #0000001F', ...defaultTheme.shadows.slice(2)] as Shadows,
    breakpoints: { values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 } },
    palette: {
      primary: { main, light, dark, contrastText, accent, darkest, separator },
      action: { hover: light, focus: dark },
      ...(divider && { divider }),
      secondary: {
        main: '#3366FF', // Minimal blue
        light: '#84A9FF',
        dark: '#1939B7',
        contrastText,
      },
      neutral: {
        // Shares a literal with the default `textSecondary` but is not the same role: this is
        // `color.neutral.secondary`, the mid grey for icons and UI edges, which owes 3:1 (it has
        // 4.27+) and is never text. A brand opting into the darker text grey does not move it.
        main: '#6D717F',
        light: '#DCDCDC',
        dark: '#4B4F5C',
        accent: '#E2E4E9',
        contrastText,
      },
      warning: { main: warningMain, ...STATUS_AS_BUILT.warning, contrastText },
      info: { main: STATUS.info.main, ...STATUS_AS_BUILT.info, contrastText },
      success: { main: STATUS.success.main, ...STATUS_AS_BUILT.success, contrastText },
      error: { main: STATUS.error.main, ...STATUS_AS_BUILT.error, contrastText },
      background: {
        // Canvas/paper are opt-in refresh knobs (Trackd = soft-grey canvas + crisp white paper
        // so cards separate; others keep the originals).
        default: canvas,
        paper,
        panel: '#F5F5F5',
      },
      text: { ...NEUTRAL.text, secondary: textSecondary, link: linkText },
      grey: {
        '50': '#F9F9F9',
        '100': '#F2F2F2',
        '200': '#E9E9E9',
        '900': '#1F202430',
      },
    },

    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 400,
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: `
        body {
          -webkit-font-smoothing: subpixel-antialiased;
          -moz-osx-font-smoothing: auto;
        }
      `,
      },
      MuiTypography: {
        variants: [
          {
            props: { variant: 'caption' },
            style: ({ theme }) => ({
              color: theme.palette.text.secondary,
            }),
          },
        ],
      },
      MuiIconButton: {
        variants: [
          {
            props: { color: 'primary-contained' },
            style: ({ theme }) => ({
              background: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': { background: theme.palette.primary.dark },
            }),
          },
          {
            props: { color: 'primary-outlined' },
            style: ({ theme }) => ({
              outline: `1.5px solid ${theme.palette.primary.main}`,
              color: theme.palette.primary.main,
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              padding: '4px',
            }),
          },
        ],
      },
      MuiAppBar: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderBottomLeftRadius: inputRadius,
            borderBottomRightRadius: inputRadius,
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.main,
            boxShadow: defaultTheme.shadows[0], // none
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          }),
        },
      },
      MuiLink: {
        styleOverrides: {
          // `text.link` rather than `info.main`, so a brand can move its links without moving the
          // blue its icons and edges use: link text is body-size and owes 4.5:1, which `info.main`
          // `#0288D1` reaches on no surface at all (3.19–3.86). Trackd opts into `#0269A3` (5.0+);
          // the rest default `text.link` to `info.main` and are unchanged. Hover stays `info.dark`,
          // darker than either (6.04+), so a link still darkens on hover.
          root: ({ theme }) => ({
            color: theme.palette.text.link,
            '&:visited': { color: theme.palette.text.link },
            '&:hover': { color: theme.palette.info.dark },
          }),
        },
      },
      MuiButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: inputRadius,
            textTransform: 'none',
            boxShadow: 'none',
            padding: '7px 14px',
            minWidth: '32px',
            whiteSpace: 'nowrap',
            pointerEvents: 'auto',
            '&.Mui-disabled': {
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.text.disabled}`,
              color: theme.palette.text.disabled,
            },
          }),
        },

        variants: [
          // Primary variant - all colors
          ...(['primary', 'info', 'success', 'error', 'warning', 'neutral'] as const).map(
            (color) => ({
              props: { variant: 'primary' as const, color },
              style: ({ theme }: { theme: Theme }) => {
                const disabledBackground = theme.palette.neutral.light;
                const disabledText = theme.palette.text.disabled;

                const primaryColor = theme.palette[color];
                const textColor = primaryColor.contrastText;
                const baseColor = primaryColor.main;
                const hoverColor = primaryColor.dark;

                return {
                  color: textColor,
                  backgroundColor: baseColor,
                  border: `1px solid ${baseColor}`,
                  '&:hover': { backgroundColor: hoverColor, borderColor: hoverColor },
                  '&.Mui-disabled': {
                    color: disabledText,
                    backgroundColor: disabledBackground,
                    border: `1px solid ${disabledBackground}`,
                  },
                };
              },
            }),
          ),
          // Secondary variant - all colors
          ...(['primary', 'info', 'success', 'error', 'warning', 'neutral'] as const).map(
            (color) => ({
              props: { variant: 'secondary' as const, color },
              style: ({ theme }: { theme: Theme }) => {
                const colorPalette = theme.palette[color];
                return {
                  color: colorPalette.main,
                  border: `1px solid ${colorPalette.main}`,
                  '&:hover': { backgroundColor: colorPalette.light },
                };
              },
            }),
          ),
          // Text variant - all colors
          ...(['primary', 'info', 'success', 'error', 'warning', 'neutral'] as const).map(
            (color) => ({
              props: { variant: 'text' as const, color },
              style: ({ theme }: { theme: Theme }) => ({
                // current design dictates text variant should have no background
                backgroundColor: 'transparent',
                color: theme.palette[color].main,

                willChange: 'transform',
                transformOrigin: 'center center',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': { transform: 'scale(1.05)' },
                '&.Mui-disabled': BORDERLESS_DISABLED,
              }),
            }),
          ),
          // Custom variant - all colors
          ...(['primary', 'info', 'success', 'error', 'warning', 'neutral'] as const).map(
            (color) => ({
              props: { variant: 'custom' as const, color },
              style: ({ theme }: { theme: Theme }) => ({
                backgroundColor: 'transparent',
                color: theme.palette.text.primary,
                '&.Mui-disabled': BORDERLESS_DISABLED,
              }),
            }),
          ),
          // Neutral-specific variants using text.primary (excluding primary variant)
          {
            props: { variant: 'secondary', color: 'neutral' },
            style: ({ theme }: { theme: Theme }) => ({
              color: theme.palette.text.primary,
              border: `1px solid ${theme.palette.neutral.main}`,
              '&:hover': { backgroundColor: theme.palette.neutral.light },
            }),
          },
          {
            props: { variant: 'text', color: 'neutral' },
            style: ({ theme }: { theme: Theme }) => ({ color: theme.palette.text.primary }),
          },
        ],
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: inputRadius,
            boxShadow: 'none',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: inputRadius,
            boxShadow: 'none',
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            // iOS Safari/WKWebView zooms inputs below 16px on focus and can lock the zoom.
            // Our app is configured for 14px, set inputs to 16px on iPhone WebKit to avoid this issue.
            // 767px is the narrowest support iPad breakpoint
            '@supports (-webkit-touch-callout: none)': {
              '@media (max-width: 767px)': {
                fontSize: NO_FOCUS_ZOOM_FONT_SIZE,
              },
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          fullWidth: true,
          variant: 'outlined',
          helperText: '\u00A0', // default an empty character to prevent UI shift, can be overridden by passing prop
          slots: { formHelperText: FieldHelpText },
        },
      },
      MuiPickersTextField: {
        defaultProps: {
          helperText: '\u00A0',
          slots: { formHelperText: FieldHelpText },
        },
        variants: [
          {
            props: { color: 'warning' },
            style: ({ theme }: { theme: Theme }) => ({
              '& .MuiPickersOutlinedInput-root:not(.Mui-disabled) fieldset': {
                borderColor: `${theme.palette.warning.main} !important`,
              },
              '& .MuiPickersOutlinedInput-root:hover:not(.Mui-disabled) fieldset': {
                borderColor: theme.palette.warning.dark,
              },
              '& .MuiPickersOutlinedInput-root.Mui-focused:not(.Mui-disabled) fieldset': {
                borderColor: theme.palette.warning.main,
              },
            }),
          },
        ],
      },
      MuiPickersOutlinedInput: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            borderRadius: inputRadius, // opt-in knob (Trackd = 4px inputs; others keep 16px)
            backgroundColor: theme.palette.background.paper,
            minHeight: BUTTON_SIZES.large,
            '&.MuiPickersInputBase-inputSizeSmall': { minHeight: BUTTON_SIZES.medium },
            '& .MuiPickersInputBase-sectionsContainer': { padding: '14px 0' },
            '& fieldset': { borderColor: theme.palette.divider },
            '&:hover:not(.Mui-disabled) fieldset': { borderWidth: '2px' },
            '&:hover:not(.Mui-disabled):not(.Mui-error) fieldset': {
              borderColor: theme.palette.primary.main,
            },
            '&.Mui-focused fieldset': { borderWidth: '2px' },
            '&.Mui-disabled': {
              backgroundColor: theme.palette.background.paper,
              '& fieldset': { borderColor: `${theme.palette.divider} !important` },
              '& .MuiPickersInputBase-input, & .MuiPickersInputBase-sectionsContainer': {
                WebkitTextFillColor: theme.palette.text.disabled,
              },
            },
          }),
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            minWidth: '128px',
            borderRadius: inputRadius, // opt-in knob (Trackd = 4px inputs; others keep 16px)
            backgroundColor: theme.palette.background.paper,
            minHeight: BUTTON_SIZES.large,
            '&.MuiInputBase-sizeSmall': { minHeight: BUTTON_SIZES.medium },
            '& .MuiOutlinedInput-input': { padding: '14px' },
            '&.MuiInputBase-sizeSmall .MuiOutlinedInput-input': { padding: '8.5px 14px' },
            '& .MuiSelect-select.MuiOutlinedInput-input': { paddingRight: '32px' },
            '&.MuiInputBase-sizeSmall .MuiSelect-select.MuiOutlinedInput-input': {
              padding: '8.5px 32px 8.5px 14px',
            },
            '& textarea.MuiInputBase-input.MuiOutlinedInput-input': { padding: '0 !important' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
            '&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': { borderWidth: '2px' },
            '&:hover:not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: '2px' },
            '&.Mui-disabled': {
              backgroundColor: theme.palette.background.paper,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
              '& .MuiOutlinedInput-input': { WebkitTextFillColor: theme.palette.text.disabled },
            },
          }),
        },
        variants: FIELD_STATE_COLOURS.map((state) => ({
          props: { color: state },
          style: ({ theme }: { theme: Theme }) => ({
            '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette[state].main },
            '&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
              borderColor: `${theme.palette[state].main} !important`,
            },
          }),
        })),
      },
      MuiInputLabel: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.secondary,
            '&.Mui-disabled': { color: theme.palette.text.disabled },
          }),
        },
        variants: FIELD_STATE_COLOURS.map((state) => ({
          props: { color: state },
          style: ({ theme }: { theme: Theme }) => ({ color: theme.palette[state].main }),
        })),
      },
      MuiFormControl: {
        defaultProps: { fullWidth: true },
        styleOverrides: {
          // Form rhythm is a single-source `!important` rhythm — every brand currently keeps the
          // original 0.75rem via `fieldMargin`'s default. A layout that genuinely needs a
          // different rhythm for one field should opt OUT with the `MuiFormControl-noMargin` class
          // (see `controlled-checkbox.tsx`'s `inline` prop) and set its own margin via `sx` or
          // parent container's gap prop, not by changing this shared knob — `!important` means any
          // field skipping that opt-out still gets its explicit margin silently discarded.
          root: {
            '&:not(.MuiFormControl-noMargin)': {
              marginTop: `${fieldMargin} !important`,
              marginBottom: `${fieldMargin} !important`,
            },
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            [defaultTheme.breakpoints.up('xs')]: { minHeight: 48 },
            [defaultTheme.breakpoints.up('sm')]: { minHeight: 48 },
            [defaultTheme.breakpoints.up('md')]: { minHeight: 48 },
          },
          dense: { minHeight: 32 }, // For the dense variant
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          root: { '& .MuiOutlinedInput-root .MuiAutocomplete-input': { padding: '5px' } },
          listbox: ({ theme }) => ({
            '& .MuiAutocomplete-option': {
              [theme.breakpoints.down('lg')]: {
                minHeight: 48,
                paddingTop: 8,
                paddingBottom: 8,
              },
              '&.Mui-focused': { backgroundColor: theme.palette.primary.light },
              '&[aria-selected="true"]': { backgroundColor: theme.palette.primary.light },
            },
          }),
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: ({ theme }) => ({
            '&.Mui-focusVisible': { backgroundColor: theme.palette.primary.light },
          }),
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: ({ theme }) => ({
            backgroundColor: theme.palette.neutral.dark,
            color: theme.palette.neutral.contrastText,
            border: '1px solid',
            borderColor: theme.palette.primary.separator,
          }),
          popper: ({ theme }) => ({
            color: theme.palette.neutral.contrastText,
          }),
          arrow: ({ theme }) => ({
            color: theme.palette.neutral.dark,
            '&::before': { border: '1px solid', borderColor: theme.palette.primary.separator },
          }),
        },
      },
    },
  });
};

/**
 * Trackd records *when an incident occurred* on a legal register, so its time pickers step by the
 * minute rather than MUI's default five. Five-minute buckets round a compliance timestamp to
 * something that did not happen, and they also strand the wizard's own default: the Details step
 * seeds the incident time to `now` (an exact minute like 01:26), which is then not a value the
 * picker can return to once the guard has moved off it.
 *
 * Set as a theme default rather than per call site so every Trackd date-time field agrees — the
 * incident time, the CCTV and police blocks, and shift start/end are all "when did this happen"
 * fields. Scoped to this theme, so Checkd, Manager and iDU-Dashboard keep MUI's default.
 */
const TRACKD_TIME_STEPS = { hours: 1, minutes: 1 };

// Trackd theme (Incident Register) - Purple
const TrackdBaseTheme = CustomTheme({
  primary: { ...TRACKD_PRIMARY_THEME },
  // Trackd opts into the design-system visual refresh (grey canvas, 4px inputs, tighter form
  // rhythm). Checkd/Dashd/Manager below stay on the original defaults — unchanged.
  // Canvas, paper and input radius now come from tokens.json; they resolve to the same values
  // these literals held, so this is a wiring change and not a visual one.
  canvas: BRAND.trackd.canvas,
  paper: NEUTRAL.paper,
  inputRadius: BRAND.trackd.radiusBase,
  // Trackd's fields sit on the `#F0F0F2` canvas, where `error.main` is 4.37:1 — below AA. The
  // `error.dark` step is 8.93:1 there and 10.16:1 on paper, so it clears both surfaces.
  fieldErrorText: STATUS.error.dark,
  /* Trackd is the only brand opted into the §1B contrast corrections, for the same reason it is the
     canary for the token pipeline: it is not yet live, and its `#F0F0F2` canvas is the surface that
     costs ~12% of every ratio measured on white. All three keep their hue and are a step darker,
     not a different colour.
       textSecondary  #6D717F -> #5F626D   4.12 worst -> 5.15
       warningMain    #F26316 -> #D9540C   2.81 worst -> 3.53, the `.main` 3:1 UI/large-text bar
       linkText       #0288D1 -> #0269A3   3.26 worst -> 5.00, every link, at body size */
  textSecondary: NEUTRAL.text.secondary,
  warningMain: STATUS.warning.main,
  linkText: NEUTRAL.text.link,
  // Selection rows on the denser Trackd ramp (theme.trackd.* in tokens.json — the prototype's
  // `.check` row). CSS custom properties because that is how the brand-scoped scales are
  // published; tokens.css is loaded app-wide by Trackd's root.tsx, so the var is the real source
  // and the literal is only a fallback for environments that never mount the stylesheet (jsdom).
  selectionRow: {
    paddingBlock: 'var(--trackd-space-5, 10px)',
    paddingInline: 'var(--trackd-space-6, 12px)',
    gap: 'var(--trackd-space-4, 8px)',
    radius: 'var(--trackd-radius-sm, 10px)',
    minHeight: 'var(--size-touch-min, 44px)',
    // Prototype rows stay on paper when selected — the purple lives in the border + indicator.
    selectedBackground: 'background.paper',
    indicator: 'checkbox',
  },
});

/**
 * The `MuiInputBase` guard above stops iOS auto-zoom for text fields, but it cannot reach a picker:
 * MUI X fields render `MuiPickersInputBase`, a separate component, so none of the picker's elements
 * carry the guarded class. Measured in the incident wizard at 375px, the field a tap lands on — the
 * `contenteditable` section span — computed to 14px, and the input behind it to 13.3px. Both are
 * under WebKit's 16px threshold, so tapping a date-time zoomed the viewport with no way back:
 * Trackd's viewport meta has no `maximum-scale` and the page cannot scroll horizontally.
 *
 * The section spans inherit from the root; the `<input>` does not (a UA default beats inheritance),
 * so it is set explicitly. Same `@supports`/`@media` condition as the text-field guard — iOS WebKit
 * only — so no other platform's rendering moves. Lives on TrackdTheme rather than the shared base
 * because Checkd is live and must stay pixel-identical.
 */
const IOS_PICKER_ZOOM_GUARD = {
  '@supports (-webkit-touch-callout: none)': {
    '@media (max-width: 767px)': {
      fontSize: NO_FOCUS_ZOOM_FONT_SIZE,
      '& .MuiPickersInputBase-input': { fontSize: NO_FOCUS_ZOOM_FONT_SIZE },
    },
  },
};

export const TrackdTheme = createTheme(TrackdBaseTheme, {
  components: {
    MuiDateTimePicker: { defaultProps: { timeSteps: TRACKD_TIME_STEPS } },
    MuiPickersInputBase: { styleOverrides: { root: IOS_PICKER_ZOOM_GUARD } },
  },
});

// Checkd theme - Magenta
export const CheckdTheme = CustomTheme({ primary: { ...CHECKD_PRIMARY_THEME } });
// Dashd theme (Internal dashboard) - Blue
export const DashdTheme = CustomTheme({ primary: { ...DASHD_PRIMARY_THEME } });

// Manager theme - Default theme
export const WebPortalManagerTheme = CustomTheme();

// Export default theme
export const theme = TrackdTheme;

export default theme;
